import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { convertVideoCoordinates } from '../../utils/annotationUtils';
import { VIDEO_WIDTH, VIDEO_HEIGHT, BALL_NOT_IN_FRAME } from '../../utils/constants';
import { getFrameTolerance, snapToFrame, normalizeTimeToFrame } from '../../utils/timeUtils';
import { getFPS } from '../../utils/constants';
import { getFrameForTime } from '../../utils/frameIndex';

const AnnotationLayerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: ${props => props.displayWidth || VIDEO_WIDTH}px;
  height: ${props => props.displayHeight || VIDEO_HEIGHT}px;
  z-index: ${props => props.isClickable ? 4 : 3};
  pointer-events: ${props => props.isClickable ? 'auto' : 'none'};
  cursor: ${props => props.isClickable ? 'crosshair' : 'default'};
  background: transparent;
`;

const AnnotationDot = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: transparent;
  border: 3px solid ${props => props.color};
  pointer-events: none;
  z-index: 3;
  box-sizing: border-box;
  transform: translate(-10px, -10px);
`;

const BoundingBox = styled.div`
  position: absolute;
  border: 2px solid ${props => props.color};
  background-color: transparent;
  pointer-events: none;
  z-index: 2;
  box-sizing: border-box;
`;


const CornerCircle = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color};
  border: 2px solid ${props => props.color};
  pointer-events: ${props => props.isInteractive ? 'auto' : 'none'};
  cursor: ${props => props.isInteractive ? 'grab' : 'default'};
  z-index: 5;
  box-sizing: border-box;
  transform: translate(-8px, -8px);
  
  &:hover {
    box-shadow: ${props => props.isInteractive ? '0 0 12px white' : 'none'};
    transition: box-shadow 0.2s ease;
  }
  
  &:active {
    cursor: ${props => props.isInteractive ? 'grabbing' : 'default'};
  }
`;

const StrokeLabel = styled.div`
  position: absolute;
  font-size: 10px;
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  background: rgba(40, 42, 54, 0.6);
  padding: 2px 4px;
  border-radius: 3px;
  pointer-events: none;
  z-index: 4;
  white-space: nowrap;
  transform: ${props => props.transform};
  margin-top: ${props => props.marginTop};
  margin-left: ${props => props.marginLeft};
`;

const PlayerText = styled.span`
  color: ${props => props.color};
`;

const ShotText = styled.span`
  color: ${props => props.color};
`;

// Stroke Signal Components
const StrokeSignalContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 3;
  pointer-events: none;
  will-change: transform;
`;

const SignalBar = styled.div`
  width: 12px;
  background-color: ${theme.colors.borderLight};
  border-radius: 6px;
  border: 1px solid ${theme.colors.border};
  position: relative;
  overflow: hidden;
  ${props => props.isHighConfidence && `
    box-shadow: 0 0 8px ${props.strokeColor};
    border-color: ${props.strokeColor};
    border-width: 2px;
  `}
`;

const SignalFill = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  background-color: ${props => props.strokeColor};
`;

const ThresholdLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background-color: ${theme.colors.text};
  opacity: 0.6;
  z-index: 1;
`;

const StrokeTypeLabel = styled.div`
  margin-top: 4px;
  padding: 2px 4px;
  background-color: ${props => props.strokeColor};
  color: ${theme.colors.background};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeights.bold};
  border-radius: 3px;
  min-width: 20px;
  text-align: center;
  font-family: ${theme.typography.fontFamily};
`;

const NotInFrameBadge = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: ${theme.typography.fontWeights.medium};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.textSecondary};
  background: rgba(40, 42, 54, 0.8);
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  pointer-events: none;
  z-index: 4;
`;

const CustomLabelBadge = styled.div`
  position: absolute;
  font-size: 9px;
  font-weight: ${theme.typography.fontWeights.bold};
  font-family: ${theme.typography.fontFamily};
  background: rgba(40, 42, 54, 0.7);
  color: #F8F8F2;
  padding: 1px 4px;
  border-radius: 3px;
  pointer-events: none;
  z-index: 5;
  white-space: nowrap;
  transform: translate(-50%, 14px);
`;

const AnnotationLayer = ({ 
  annotations, 
  allAnnotations = null, // Pass all annotations for frame-accurate filtering during playback
  annotationsByFrame = null, // Frame-indexed Map for O(1) lookups (preferred over allAnnotations)
  playerAnnotations = [],
  allPlayerAnnotations = null, // Pass all player annotations for frame-accurate filtering
  playerAnnotationsByFrame = null, // Frame-indexed Map for O(1) lookups (preferred over allPlayerAnnotations)
  playerIdMap = {},
  videoRef, 
  zoom, 
  pan, 
  onClick, 
  onContextMenu,
  onMouseDown,
  isClickable = false,
  currentTime = 0,
  isPlayerMode = false,
  isPaused = false,
  onPlayerAnnotationChange,
  displayDimensions = { width: VIDEO_WIDTH, height: VIDEO_HEIGHT }
}) => {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragAnnotationId, setDragAnnotationId] = useState(null);
  const [dragCornerType, setDragCornerType] = useState(null);
  const annotationLayerRef = useRef(null);
  
  // With WebCodecs, currentTime is always frame-accurate (updated per decoded frame).
  // No requestVideoFrameCallback needed.
  
  // Determine which annotations to display - use frame-accurate time during playback
  // Uses frame-indexed Map for O(1) lookup instead of O(n) filter when available
  const displayAnnotations = useMemo(() => {
    // If neither frame index nor allAnnotations provided, use pre-filtered annotations prop
    const hasFrameIndex = annotationsByFrame && annotationsByFrame.size > 0;
    if (!hasFrameIndex && !allAnnotations) return annotations;
    
    // currentTime is frame-accurate (from WebCodecs onTimeUpdate)
    const annotationFrame = getFrameForTime(currentTime);
    
    // O(1) Map lookup (preferred) vs O(n) filter (fallback)
    if (hasFrameIndex) {
      const baseAnnotations = annotationsByFrame.get(annotationFrame) || [];
      
      // Option A: Lingering dot with fade — keep bounce/net dots visible for several frames, fading out
      const LINGER_FRAMES = 20;
      const LINGER_TYPES = new Set(['bounce', 'net']);
      const recentLinger = [];
      for (let offset = 1; offset <= LINGER_FRAMES; offset++) {
        const pastFrame = annotationFrame - offset;
        if (pastFrame < 0) break;
        const pastAnns = annotationsByFrame.get(pastFrame);
        if (!pastAnns) continue;
        for (const ann of pastAnns) {
          if (LINGER_TYPES.has(ann.type)) {
            recentLinger.push({
              ...ann,
              _lingerFade: true,
              _fadeOpacity: 1 - (offset / LINGER_FRAMES),
            });
          }
        }
      }
      
      return recentLinger.length > 0 ? [...baseAnnotations, ...recentLinger] : baseAnnotations;
    }
    
    // Fallback: O(n) filter for backward compatibility
    const currentFPS = getFPS();
    if (!currentFPS) return annotations;
    const tolerance = getFrameTolerance(currentFPS);
    const snappedTime = snapToFrame(currentTime, currentFPS);
    return allAnnotations.filter(ann =>
      Math.abs(ann.timestamp - snappedTime) < tolerance
    );
  }, [annotationsByFrame, allAnnotations, annotations, currentTime]);
  
  // Similarly for player annotations - O(1) Map lookup when available
  const displayPlayerAnnotations = useMemo(() => {
    const hasFrameIndex = playerAnnotationsByFrame && playerAnnotationsByFrame.size > 0;
    if (!hasFrameIndex && !allPlayerAnnotations) return playerAnnotations;
    
    const annotationFrame = getFrameForTime(currentTime);
    
    // O(1) Map lookup (preferred) vs O(n) filter (fallback)
    if (hasFrameIndex) {
      return playerAnnotationsByFrame.get(annotationFrame) || [];
    }
    
    // Fallback: O(n) filter for backward compatibility
    const currentFPS = getFPS();
    if (!currentFPS) return playerAnnotations;
    const tolerance = getFrameTolerance(currentFPS);
    const snappedTime = snapToFrame(effectiveTime, currentFPS);
    return allPlayerAnnotations.filter(ann =>
      Math.abs(ann.timestamp - snappedTime) < tolerance
    );
  }, [playerAnnotationsByFrame, allPlayerAnnotations, playerAnnotations, currentTime]);
  
  // Handle mouse down on corner circle
  const handleCornerMouseDown = (e, playerAnnotation, cornerType) => {
    if (!isPlayerMode || !isPaused) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (!annotationLayerRef.current) return;
    
    const rect = annotationLayerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert mouse position to video coordinates using same formula as CourtOverlay
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;
    
    // Get current corner position in native coordinates
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    const currentCorner = cornerType === 'corner1' 
      ? { x: playerAnnotation.boundingBox.x1, y: playerAnnotation.boundingBox.y1 }
      : { x: playerAnnotation.boundingBox.x2, y: playerAnnotation.boundingBox.y2 };
    
    // Convert corner from native video coordinates to display coordinates (without zoom/pan)
    const { x: baseDisplayCornerX, y: baseDisplayCornerY } = convertVideoCoordinates(
      currentCorner.x, currentCorner.y, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
    );
    
    setDragOffset({
      x: videoX - baseDisplayCornerX,
      y: videoY - baseDisplayCornerY
    });
    setDragAnnotationId(playerAnnotation.id);
    setDragCornerType(cornerType);
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!dragAnnotationId || !dragCornerType) return;
    
    if (!annotationLayerRef.current) return;
    
    const rect = annotationLayerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert mouse position to video coordinates using same formula as CourtOverlay
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;
    
    // Calculate new position in display coordinates (without zoom/pan)
    const newDisplayX = videoX - dragOffset.x;
    const newDisplayY = videoY - dragOffset.y;
    
    // Clamp to display bounds
    const clampedDisplayX = Math.max(0, Math.min(newDisplayX, displayDimensions.width));
    const clampedDisplayY = Math.max(0, Math.min(newDisplayY, displayDimensions.height));
    
    // Convert back to native video coordinates
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    // Convert display coordinates to native video coordinates
    const nativeX = (clampedDisplayX / displayDimensions.width) * nativeWidth;
    const nativeY = (clampedDisplayY / displayDimensions.height) * nativeHeight;
    
    // Clamp to native video bounds
    const clampedX = Math.max(0, Math.min(nativeX, nativeWidth));
    const clampedY = Math.max(0, Math.min(nativeY, nativeHeight));
    
    // Find the current player annotation
    const currentAnnotation = playerAnnotations.find(ann => ann.id === dragAnnotationId);
    if (!currentAnnotation) return;
    
    // Update the bounding box
    const updatedBoundingBox = {
      ...currentAnnotation.boundingBox,
      [dragCornerType === 'corner1' ? 'x1' : 'x2']: clampedX,
      [dragCornerType === 'corner1' ? 'y1' : 'y2']: clampedY
    };
    
    // Call the callback immediately on every move (exactly like court keypoints)
    if (onPlayerAnnotationChange) {
      onPlayerAnnotationChange(currentAnnotation.player, updatedBoundingBox);
    }
  };

  // Handle mouse up for dragging
  const handleMouseUp = () => {
    // Clear drag state (exactly like court keypoints)
    setDragOffset({ x: 0, y: 0 });
    setDragAnnotationId(null);
    setDragCornerType(null);
  };

  // Add mouse event listeners for dragging
  useEffect(() => {
    if (dragAnnotationId) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragAnnotationId, dragOffset]);
  
  // Get current player annotations for display - use displayPlayerAnnotations which handles frame-accurate timing
  const getCurrentPlayerAnnotations = () => {
    return displayPlayerAnnotations;
  };

  // Helper to apply zoom/pan to a point (same as CourtOverlay)
  const applyZoomPan = (pt) => ({
    x: pt.x * zoom - pan.x,
    y: pt.y * zoom - pan.y
  });

  const renderAnnotation = (annotation, index) => {
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;

    // Ball not in frame: no position to show, only a small badge
    if (annotation.x === BALL_NOT_IN_FRAME && annotation.y === BALL_NOT_IN_FRAME) {
      return (
        <NotInFrameBadge key={annotation.id}>
          Not in frame {annotation.type ? `(${annotation.type})` : ''}
        </NotInFrameBadge>
      );
    }

    // Convert from native video coordinates to display coordinates
    const { x: baseDisplayX, y: baseDisplayY } = convertVideoCoordinates(
      annotation.x, 
      annotation.y, 
      nativeWidth, 
      nativeHeight,
      displayDimensions.width,
      displayDimensions.height
    );
    
    // Apply zoom and pan transforms
    const { x: displayX, y: displayY } = applyZoomPan({ x: baseDisplayX, y: baseDisplayY });

    // Get color based on type
    const getColor = (type) => {
      switch (type) {
        case 'stroke': return theme.colors.stroke;
        case 'bounce': return theme.colors.bounce;
        case 'in-flight': return theme.colors.inFlight;
        case 'net': return theme.colors.net;
        default: return theme.colors.text;
      }
    };

    // Option A: Lingering bounce/net dot — render just the dot with fading opacity
    if (annotation._lingerFade) {
      return (
        <AnnotationDot
          key={`linger-${annotation.id}-${Math.round(annotation._fadeOpacity * 100)}`}
          color={getColor(annotation.type)}
          style={{
            left: Math.round(displayX),
            top: Math.round(displayY),
            opacity: annotation._fadeOpacity,
          }}
        />
      );
    }

    // Get player and shot type colors
    const getPlayerColor = (player) => {
      switch (player) {
        case 'bottom': return '#50fa7b'; // Dracula green
        case 'top': return '#ff5555'; // Dracula red
        default: return theme.colors.text;
      }
    };

    const getShotTypeColor = (shotType) => {
      switch (shotType) {
        case 'forehand': return '#52c3a3'; // Teal green
        case 'backhand': return '#db722c'; // Orange
        case 'serve': return '#f8f9f2'; // Light grey/white
        case 'smash': return '#f1fa8c'; // Dracula yellow
        default: return theme.colors.text;
      }
    };

    // Calculate smart label positioning
    const getLabelPosition = (x, y) => {
      const margin = 25;
      let transform = 'translateY(-100%)';
      let marginTop = -margin;
      let marginLeft = 0;
      
      if (y < margin) {
        transform = 'translateY(0)';
        marginTop = margin;
      }
      
      if (x < margin) {
        marginLeft = margin;
      } else if (x > displayDimensions.width - margin) {
        marginLeft = -margin;
      }
      
      return { transform, marginTop, marginLeft };
    };

    // Render bounding box if coordinates exist
    const renderBoundingBox = () => {
      if (!annotation.boundingBox) return null;
      
      const { x1, y1, x2, y2 } = annotation.boundingBox;
      
      // Convert bounding box coordinates to display coordinates
      const { x: baseDisplayX1, y: baseDisplayY1 } = convertVideoCoordinates(
        x1, y1, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
      );
      const { x: baseDisplayX2, y: baseDisplayY2 } = convertVideoCoordinates(
        x2, y2, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
      );
      
      // Apply zoom and pan transforms
      const { x: displayX1, y: displayY1 } = applyZoomPan({ x: baseDisplayX1, y: baseDisplayY1 });
      const { x: displayX2, y: displayY2 } = applyZoomPan({ x: baseDisplayX2, y: baseDisplayY2 });
      
      // Calculate box dimensions and position
      const left = Math.min(displayX1, displayX2);
      const top = Math.min(displayY1, displayY2);
      const width = Math.abs(displayX2 - displayX1);
      const height = Math.abs(displayY2 - displayY1);
      
      // Get player color for the bounding box
      const playerColor = getPlayerColor(annotation.player);
      
      return (
        <BoundingBox
          key={`bbox-${annotation.id}`}
          color={playerColor}
          style={{
            left: Math.round(left),
            top: Math.round(top),
            width: Math.round(width),
            height: Math.round(height),
          }}
        />
      );
    };
    
    return (
      <React.Fragment key={annotation._pulseOnly ? `pulse-${annotation.id}` : annotation.id}>
        {/* Render bounding box first (behind other elements) */}
        {renderBoundingBox()}
        
        <AnnotationDot
          color={getColor(annotation.type)}
          style={{
            left: Math.round(displayX),
            top: Math.round(displayY),
          }}
        />
        {annotation.customLabel && annotation.customLabel !== 'none' && (
          <CustomLabelBadge
            style={{
              left: Math.round(displayX),
              top: Math.round(displayY),
            }}
          >
            {annotation.customLabel}
          </CustomLabelBadge>
        )}
        {annotation.type === 'stroke' && annotation.player && annotation.strokeType && (
          <StrokeLabel
            style={{
              left: Math.round(displayX),
              top: Math.round(displayY),
            }}
            transform={getLabelPosition(displayX, displayY).transform}
            marginTop={getLabelPosition(displayX, displayY).marginTop}
            marginLeft={getLabelPosition(displayX, displayY).marginLeft}
          >
            (<PlayerText color={getPlayerColor(annotation.player)}>{annotation.player}</PlayerText>, <ShotText color={getShotTypeColor(annotation.strokeType)}>{annotation.strokeType}</ShotText>)
          </StrokeLabel>
        )}
      </React.Fragment>
    );
  };

  // Get stroke type color
  const getStrokeTypeColor = (strokeType) => {
    switch (strokeType) {
      case 'forehand': return '#52c3a3'; // Teal green
      case 'backhand': return '#db722c'; // Orange
      case 'serve': return '#f8f9f2'; // Light grey/white
      case 'smash': return '#f1fa8c'; // Dracula yellow
      default: return theme.colors.text;
    }
  };

  // Render stroke signal bar
  const renderStrokeSignal = (playerAnnotation, left, top, width, height) => {
    const { strokeScore = 0, strokeType } = playerAnnotation;
    
    // Only show if strokeScore exists and is greater than 0
    if (!strokeScore || strokeScore <= 0) return null;
    
    // Use fixed positioning relative to bounding box to avoid zoom jitter
    const signalBarHeight = height * 0.8; // 80% of bounding box height
    const signalBarX = playerAnnotation.player === 'top' 
      ? left - 30  // Left side for top player
      : left + width + 15; // Right side for bottom player
    const signalBarY = top + height * 0.1; // Start 10% from top
    
    const fillHeight = signalBarHeight * Math.min(strokeScore, 1); // Cap at 100%
    const thresholdHeight = signalBarHeight * 0.75; // 75% threshold line position
    
    // Determine confidence level and colors
    const isHighConfidence = strokeScore > 0.75;
    const strokeColor = getStrokeTypeColor(strokeType);
    const fillColor = isHighConfidence ? strokeColor : theme.colors.textSecondary; // Gray for low confidence
    
    // Show stroke type label only if high confidence
    const showStrokeType = isHighConfidence && strokeType;
    const strokeTypeAbbrev = strokeType ? strokeType.substring(0, 2).toUpperCase() : '';
    
    return (
      <StrokeSignalContainer
        key={`stroke-signal-${playerAnnotation.id}`}
        style={{
          transform: `translate(${signalBarX}px, ${signalBarY}px)`,
        }}
      >
        <SignalBar 
          style={{ height: signalBarHeight }}
          isHighConfidence={isHighConfidence}
          strokeColor={strokeColor}
        >
          {/* Signal fill */}
          <SignalFill 
            strokeColor={fillColor}
            style={{ height: fillHeight }}
          />
          {/* 75% threshold line */}
          <ThresholdLine 
            style={{ bottom: thresholdHeight }}
          />
        </SignalBar>
        {showStrokeType && (
          <StrokeTypeLabel strokeColor={strokeColor}>
            {strokeTypeAbbrev}
          </StrokeTypeLabel>
        )}
      </StrokeSignalContainer>
    );
  };

  const renderPlayerAnnotation = (playerAnnotation) => {
    const nativeWidth = (videoRef?.current?.width) || VIDEO_WIDTH;
    const nativeHeight = (videoRef?.current?.height) || VIDEO_HEIGHT;
    
    const { x1, y1, x2, y2 } = playerAnnotation.boundingBox;
    
    // Convert bounding box coordinates to display coordinates
    const { x: baseDisplayX1, y: baseDisplayY1 } = convertVideoCoordinates(
      x1, y1, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
    );
    const { x: baseDisplayX2, y: baseDisplayY2 } = convertVideoCoordinates(
      x2, y2, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
    );
    
    // Apply zoom and pan transforms
    const { x: displayX1, y: displayY1 } = applyZoomPan({ x: baseDisplayX1, y: baseDisplayY1 });
    const { x: displayX2, y: displayY2 } = applyZoomPan({ x: baseDisplayX2, y: baseDisplayY2 });
    
    // Calculate box dimensions and position
    const left = Math.min(displayX1, displayX2);
    const top = Math.min(displayY1, displayY2);
    const width = Math.abs(displayX2 - displayX1);
    const height = Math.abs(displayY2 - displayY1);
    
    // Get player color
    const getPlayerColor = (player) => {
      switch (player) {
        case 'bottom': return '#50fa7b'; // Dracula green
        case 'top': return '#ff5555'; // Dracula red
        default: return theme.colors.text;
      }
    };
    
    const playerColor = getPlayerColor(playerAnnotation.player);
    
    const isInteractive = isPlayerMode && isPaused;
    
    const playerName = playerIdMap[playerAnnotation.player];

    if (isPlayerMode) {
      // In player mode, render both bounding box and corner circles
      return (
        <React.Fragment key={`player-mode-${playerAnnotation.id}`}>
          <BoundingBox
            color={playerColor}
            style={{
              left: Math.round(left),
              top: Math.round(top),
              width: Math.round(width),
              height: Math.round(height),
              borderWidth: '3px',
              borderStyle: 'solid',
              opacity: 0.8
            }}
          />
          {playerName && (
            <div style={{
              position: 'absolute',
              left: Math.round(left),
              top: Math.round(top) - 22,
              color: playerColor,
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Roboto, sans-serif',
              background: 'rgba(0,0,0,0.6)',
              padding: '1px 6px',
              borderRadius: 3,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}>
              {playerName}
            </div>
          )}
          <CornerCircle
            color={playerColor}
            isInteractive={isInteractive}
            title={`Player ${playerAnnotation.player} corner (${Math.round(displayX1)}, ${Math.round(displayY1)}) - Drag to move`}
            onMouseDown={(e) => handleCornerMouseDown(e, playerAnnotation, 'corner1')}
            style={{
              left: Math.round(displayX1),
              top: Math.round(displayY1),
            }}
          />
          <CornerCircle
            color={playerColor}
            isInteractive={isInteractive}
            title={`Player ${playerAnnotation.player} corner (${Math.round(displayX2)}, ${Math.round(displayY2)}) - Drag to move`}
            onMouseDown={(e) => handleCornerMouseDown(e, playerAnnotation, 'corner2')}
            style={{
              left: Math.round(displayX2),
              top: Math.round(displayY2),
            }}
          />
          {/* Render stroke signal bar */}
          {renderStrokeSignal(playerAnnotation, left, top, width, height)}
        </React.Fragment>
      );
    } else {
      // In ball mode, render bounding box and stroke signal
      return (
        <React.Fragment key={`player-bbox-${playerAnnotation.id}`}>
          <BoundingBox
            color={playerColor}
            style={{
              left: Math.round(left),
              top: Math.round(top),
              width: Math.round(width),
              height: Math.round(height),
              borderWidth: '3px',
              borderStyle: 'solid',
              opacity: 0.8
            }}
          />
          {playerName && (
            <div style={{
              position: 'absolute',
              left: Math.round(left),
              top: Math.round(top) - 22,
              color: playerColor,
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Roboto, sans-serif',
              background: 'rgba(0,0,0,0.6)',
              padding: '1px 6px',
              borderRadius: 3,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}>
              {playerName}
            </div>
          )}
          {/* Render stroke signal bar */}
          {renderStrokeSignal(playerAnnotation, left, top, width, height)}
        </React.Fragment>
      );
    }
  };

  // Get current player annotations for display
  const currentPlayerAnnotations = getCurrentPlayerAnnotations();

  return (
    <AnnotationLayerContainer
      ref={annotationLayerRef}
      isClickable={isClickable}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseDown={onMouseDown}
      displayWidth={displayDimensions.width}
      displayHeight={displayDimensions.height}
    >
      {/* Render player annotations first (behind other elements) */}
      {currentPlayerAnnotations.map(renderPlayerAnnotation)}
      
      {/* Render regular annotations - use displayAnnotations for frame-accurate display */}
      {displayAnnotations.map(renderAnnotation)}
    </AnnotationLayerContainer>
  );
};

export default AnnotationLayer; 